import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { toast } from "sonner"
import useSWR from "swr"

import { apiFetcher } from "@/data/fetcher"
import { Settings } from "@/interfaces/Settings"

export const useFeatureCheck = (feature: string): { isEnabled: boolean|undefined, isLoading: boolean, error: Error|null } => {
  const { data: settings, isLoading, error } = useSWR<Settings>(
    '/api/v2/settings',
    apiFetcher,
  )

  if (isLoading) {
    return { isEnabled: undefined, isLoading: true, error: null }
  }

  if (error) {
    return { isEnabled: false, isLoading: false, error: error }
  }

  if (!settings) {
    return { isEnabled: false, isLoading: false, error: null }
  }

  return { isEnabled: settings.features.includes(feature), isLoading: false, error: null }
}

export const useFeatureGuard = (feature: string, redirectTo: string) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'feature',
  })
  const { isEnabled, isLoading, error } = useFeatureCheck(feature)
  const navigate = useNavigate()

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (!isEnabled) {
      toast.error(t('notEnabled'))

      navigate(redirectTo)
    }

  }, [isEnabled, isLoading, navigate, redirectTo])

  return { isEnabled, isLoading, error }
}
